import { ApolloError } from '@apollo/client';
import { GraphQLError, GraphQLFormattedError } from 'graphql';
import { useGetProductGroupById } from '@occc-app/dataAccessLayer/productGroup/queries';
import {
  useGetSurveysByWalletId,
  useGetWallet,
} from '@occc-app/dataAccessLayer/steeringWallet/queries';
import { AwardType } from '@occc-app/offerHelpers/types';
import { awardInputCreator } from '@occc-app/offerHelpers/awardInputCreator';
import { Unit_Of_Measure } from '@occc/graphql';

import { separateEveryThousandByComma } from '@occc-app/lib/numbers';
import { NO_DATA_PLACEHOLDER } from '@occc-app/design-system';
import { AwardInputType, IncentivizeResponsesFormValues, LoadedData, WalletData } from './types';

const WALLETS_BASE_URL = '/wallets';

export const routeCreator = {
  list: () => WALLETS_BASE_URL,
  create: () => `${WALLETS_BASE_URL}/create`,
  details: (id: string) => `${WALLETS_BASE_URL}/${id}`,
  update: (id: string) => `${WALLETS_BASE_URL}/${id}/update`,
};

function mapLoadedData<T>(
  loading: boolean,
  error: ApolloError | undefined,
  data: T | undefined,
): LoadedData<T> {
  return {
    loading,
    error,
    data,
  };
}

export const mapWalletData = (
  walletResult: ReturnType<typeof useGetWallet>,
  surveyResult: ReturnType<typeof useGetSurveysByWalletId>,
  productGroupResult: ReturnType<typeof useGetProductGroupById>,
): WalletData => {
  // Only one survey per wallet is supported now
  const survey = surveyResult.data?.[0];
  const wallet = walletResult.data?.steeringWallet;
  const productGroup = productGroupResult.data;

  return {
    productGroup: mapLoadedData(productGroupResult.loading, productGroupResult.error, productGroup),
    survey: mapLoadedData(surveyResult.loading, surveyResult.error, survey),
    wallet: mapLoadedData(walletResult.loading, walletResult.error, wallet),
  };
};

export const getWalletSteeringValue = (value: number, unitOfMeasure: Unit_Of_Measure) => {
  const isMoneyAmount = unitOfMeasure === Unit_Of_Measure.Amount;

  if (isMoneyAmount) {
    return `$${value}`;
  }

  return String(value);
};

const getAwardInput = ({
  awardType,
  awardPoints,
  awardVouchers,
}: IncentivizeResponsesFormValues): AwardInputType => {
  if (awardType === AwardType.AwardPoints && awardPoints) {
    return awardInputCreator.createAwardPoints(awardPoints.points);
  }

  if (awardType === AwardType.AwardVouchers && awardVouchers) {
    return awardInputCreator.createAwardVouchers(awardVouchers.voucheredOffer.id);
  }

  return null;
};

export const getSurveyOfferCreateOrUpdateInput = (
  chainId: string,
  steeringWalletUuid: string,
  incentivizeResponses: IncentivizeResponsesFormValues,
) => {
  const awardInput = getAwardInput(incentivizeResponses);

  if (awardInput && incentivizeResponses.isEnabled) {
    return {
      chainId,
      surveyUuid: steeringWalletUuid,
      ...awardInput,
    };
  }

  return {
    chainId,
    surveyUuid: steeringWalletUuid,
    awardPoints: undefined,
    awardVouchers: undefined,
  };
};

export type PossibleGraphQLError = GraphQLError | GraphQLFormattedError;
export const accumulateErrors = (
  errors: PossibleGraphQLError[],
  responseErrors: readonly PossibleGraphQLError[] = [],
): Array<PossibleGraphQLError> => [...errors, ...responseErrors];

export function formatNumberOrPlaceholder(value: number | null | undefined): string {
  return typeof value === 'number' ? separateEveryThousandByComma(value) : NO_DATA_PLACEHOLDER;
}
